import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatCompanyNumber } from '@wally-reseller/shared';
import { Skeleton } from '@wally-reseller/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@wally-reseller/ui/dropdown-menu';
import { ErrorState } from '@wally-reseller/ui/empty-state';
import { cn } from '@wally-reseller/ui/utils';
import type { PortalMeDto } from '@wally-reseller/shared';
import { ChevronsUpDown, FileText, LayoutDashboard, LogOut, UserPlus, Users } from 'lucide-react';
import { useEffect } from 'react';
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { api, ApiError } from '@/lib/api';
import { useLocale, useT } from '@/lib/i18n';
import { LanguageSwitch } from './LanguageSwitch';

/** Where to go after logging in (e.g. the lead page an email linked to). */
export const AFTER_LOGIN_KEY = 'afterLogin';

export function useMe() {
  return useQuery({ queryKey: ['me'], queryFn: api.me });
}

/**
 * Moves the session to another entity of the same person (IBGraf ↔ IBLux). Everything cached
 * belongs to the entity left behind, so it is dropped; `me` is the new one at once.
 */
export function useSwitchEntity() {
  const queryClient = useQueryClient();
  return async (partnerId: string): Promise<PortalMeDto> => {
    const me = await api.switchEntity(partnerId);
    queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
    queryClient.setQueryData(['me'], me);
    return me;
  };
}

/** The partner's name; with several entities, a menu to switch between them. */
function EntityName({ me }: { me: PortalMeDto }) {
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const switchEntity = useSwitchEntity();
  const choose = useMutation({
    mutationFn: switchEntity,
    // A lead or statement of the other entity is not found here: back to the list.
    onSuccess: () => navigate(`/${location.pathname.split('/')[1] ?? ''}`, { replace: true }),
  });
  const name = (
    <>
      <div className="truncate text-sm font-semibold">{me.partner.legalName}</div>
      <div className="truncate text-[11px] text-muted-foreground">
        {t('portal.brand')} · {formatCompanyNumber(me.partner.companyNumber)}
      </div>
    </>
  );
  if (me.entities.length < 2) return <div className="min-w-0 flex-1">{name}</div>;
  return (
    <div className="min-w-0 flex-1">
      <DropdownMenu>
        <DropdownMenuTrigger
          className="flex max-w-full items-center gap-1.5 rounded-md px-1.5 py-0.5 text-left hover:bg-muted disabled:opacity-60"
          disabled={choose.isPending}
          title={t('portal.entity.switch')}
        >
          <div className="min-w-0">{name}</div>
          <ChevronsUpDown className="size-3.5 shrink-0 text-muted-foreground" />
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuLabel>{t('portal.entity.switch')}</DropdownMenuLabel>
          <DropdownMenuRadioGroup
            value={me.partner.id}
            onValueChange={(id) => id !== me.partner.id && choose.mutate(id)}
          >
            {me.entities.map((e) => (
              <DropdownMenuRadioItem key={e.partnerId} value={e.partnerId} className="relative">
                {e.legalName}
                <span className="ml-2 text-xs text-muted-foreground">{e.country}</span>
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

const navClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    'flex h-9 shrink-0 items-center gap-2 whitespace-nowrap rounded-md px-2.5 text-sm',
    isActive ? 'bg-muted font-medium' : 'text-foreground/80 hover:bg-muted',
  );

/** Everything behind the login: the partner's name, navigation, language and sign-out. */
export function Layout() {
  const t = useT();
  const me = useMe();
  const { locale, setLocale } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const saveLocale = useMutation({ mutationFn: api.updateMe });
  const logout = useMutation({
    mutationFn: api.logout,
    onSettled: () => {
      queryClient.clear();
      navigate('/login', { replace: true });
    },
  });

  // The account's language wins over this device's last choice.
  const accountLocale = me.data?.user.locale;
  useEffect(() => {
    if (accountLocale && accountLocale !== locale) setLocale(accountLocale);
    // Only when the account's language is (re)loaded, not on every local change.
  }, [accountLocale]);

  // On a phone the navigation scrolls sideways: keep the current page's tab in view.
  useEffect(() => {
    document
      .querySelector('nav a[aria-current="page"]')
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [location.pathname, me.data]);

  if (me.isError && me.error instanceof ApiError && me.error.status === 401) {
    try {
      // localStorage: the login link from the email opens in another tab.
      localStorage.setItem(
        AFTER_LOGIN_KEY,
        JSON.stringify({ path: location.pathname, at: Date.now() }),
      );
    } catch {
      /* storage unavailable */
    }
    return <Navigate to="/login" replace />;
  }
  if (me.isError) return <ErrorState error={me.error} onRetry={() => me.refetch()} />;
  if (me.isPending) {
    return (
      <div className="mx-auto max-w-5xl p-6">
        <Skeleton className="h-6 w-48" />
      </div>
    );
  }

  const { user } = me.data;
  return (
    <div className="min-h-dvh bg-subtle">
      <header className="border-b border-border bg-background">
        <div className="mx-auto flex h-14 max-w-5xl items-center gap-3 px-4">
          <img src="/wally-128.png" alt="" width={26} height={26} className="rounded-full" />
          <EntityName me={me.data} />
          <LanguageSwitch onChange={(l) => saveLocale.mutate({ locale: l })} />
          <span className="text-xs text-muted-foreground max-sm:hidden">{user.name}</span>
          <button
            type="button"
            onClick={() => logout.mutate()}
            className="rounded p-1.5 text-muted-foreground hover:bg-muted"
            aria-label={t('portal.signOut')}
            title={t('portal.signOut')}
          >
            <LogOut className="size-4" />
          </button>
        </div>
        <nav className="mx-auto flex max-w-5xl gap-1 overflow-x-auto px-3 pb-2">
          <NavLink to="/" end className={navClass}>
            <LayoutDashboard className="size-4 text-muted-foreground" />
            {t('portal.nav.dashboard')}
          </NavLink>
          <NavLink to="/leads" className={navClass}>
            <Users className="size-4 text-muted-foreground" />
            {t('portal.nav.leads')}
          </NavLink>
          <NavLink to="/inscrire" className={navClass}>
            <UserPlus className="size-4 text-muted-foreground" />
            {t('portal.nav.register')}
          </NavLink>
          <NavLink to="/decomptes" className={navClass}>
            <FileText className="size-4 text-muted-foreground" />
            {t('portal.nav.statements')}
          </NavLink>
        </nav>
      </header>
      <main className="mx-auto max-w-5xl px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}
